import { useCallback } from 'react';
import FileSaver from 'file-saver';
import { getContext } from '@picsart/miniapps-sdk';
import mime from 'mime/lite';

import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { resizeImage } from 'pages/Batch/utils/process/resizeProcess';
import errorLogger from 'services/errorLogger';
import downloadImagesBySrc from 'utils/images/downloadImages';
import loadingImage from 'utils/images/loadingImage';
import useSubscribe from 'hooks/useSubscribe';
import useMemoSelector from 'hooks/useMemoSelector';
import analytics from 'pages/Batch/utils/analytics';
import { getAppliedPremiumTools, getBatchActiveColor, getBatchActiveTexture } from 'store/batch/selectors';
import convertBatchDataImagesFormat from 'pages/Batch/utils/convertBatchDataImagesFormat';
import { BATCH_TOOLS, IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import { downloadImage } from 'pages/Batch/utils';
import { IS_ONE_TOOL } from 'configs/constants';

import { IBatchData } from '../types';

const useDownload = () => {
  const {
    isWeb,
    allSelected,
    selectedData,
    setIsLoading,
    setIsZipFile,
    setImageFormat,
    setSelectedKeys,
    setIsSuccessModal,
    resizeParams,
    activeTool,
  } = useBatchData();

  const { subscribe, shouldSubscribe } = useSubscribe();

  const { appliedPremiumTools, activeColor, activeTexture } = useMemoSelector(state => ({
    appliedPremiumTools: getAppliedPremiumTools(state),
    activeColor: getBatchActiveColor(state),
    activeTexture: getBatchActiveTexture(state),
  }));

  const downloadImages = useCallback(async (format: IMAGE_MIME_TYPES, zipName: string) => {
    if (shouldSubscribe) {
      await subscribe();
      return;
    }

    setIsLoading(true);
    setImageFormat(format);

    let customSelectedData: IBatchData[] = selectedData;

    if (IS_ONE_TOOL && selectedData.length && activeTool === BATCH_TOOLS.removeBG_resize) {
      customSelectedData = await Promise.all(selectedData.map(async item => {
        if (!item.pendingResult?.src) {
          return {
            ...item,
          };
        }

        const image = await loadingImage(item.pendingResult?.src || item.result?.src);
        let textureImage;
        if (activeTexture?.url) {
          textureImage = await loadingImage(activeTexture.url);
        }

        const processedData = await resizeImage(
          image,
          resizeParams.width || image.width,
          resizeParams.height || image.height,
          resizeParams.usePadding,
          activeColor,
          textureImage,
        ) as { result: string; blob: Blob };

        return {
          ...item,
          result: {
            src: processedData.result,
            blob: processedData.blob,
          },
        };
      }));
    }

    const images = await convertBatchDataImagesFormat(customSelectedData, format);

    analytics.clickEvent(allSelected ? 'download_all' : selectedData.length > 1 ? 'download_selection' : 'download_single_item');

    errorLogger.leaveBreadcrumb('toolbar_click', { button: 'download', imageCount: images.length });

    setSelectedKeys([]);

    if (isWeb) {
      if (images.length === 1) {
        const imageName = images[0].name || '';
        const correctName = imageName.slice(0, imageName.lastIndexOf('.'));
        const extension = mime.getExtension(format);
        const fileName = `${correctName}.${extension}`;
        downloadImage(images[0].url, fileName);
      } else {
        const blob = await downloadImagesBySrc(images.map(({ url, name }) => ({
          url,
          name: name || zipName,
        })), zipName);

        FileSaver.saveAs(blob, `${zipName}.zip`);
        setIsZipFile(true);
      }
      analytics.download(images.length, appliedPremiumTools, format);
      setIsSuccessModal(true);
      setIsLoading(false);
      return;
    }

    try {
      await getContext().handlers.download(images.map(({ url, name }, index) => ({
        name: name || `${zipName}_${index}`,
        url,
        mimeType: format,
      })));
      setIsSuccessModal(true);
      analytics.download(images.length, appliedPremiumTools, format);
    } catch (error) {
      errorLogger.trigger(error, 'error');
    } finally {
      setIsZipFile(false);
      setIsLoading(false);
    }
  }, [
    shouldSubscribe,
    subscribe,
    appliedPremiumTools,
    selectedData,
    activeTool,
    setIsLoading,
    allSelected,
    setSelectedKeys,
    isWeb,
    setIsZipFile,
    setIsSuccessModal,
    setImageFormat,
    activeTexture?.url,
    resizeParams.width,
    resizeParams.height,
    resizeParams.usePadding,
    activeColor,
  ]);

  return {
    downloadImages,
  };
};

export default useDownload;
